import React, { useState } from 'react';
import { NavLink, Routes, Route, Navigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  MessageSquare,
  LogOut,
  ShieldCheck,
  Menu,
  X,
  Bell,
  AlertTriangle,
  Database,
  Settings,
} from 'lucide-react';
import { CampaignsPage } from '../../pages/CampaignsPage';
import { WhatsAppConnectionPage } from '../../pages/WhatsAppConnectionPage';
import { DashboardPage } from '../../pages/DashboardPage';
import { CustomersPage } from '../../pages/CustomersPage';
import { AutomationPage } from '../../pages/AutomationPage';
import { SettingsPage } from '../../pages/SettingsPage';
import { NotificationsPage } from '../../pages/NotificationsPage';
import { EventsPage } from '../../pages/EventsPage';
import { ErrorLogsPage } from '../../pages/ErrorLogsPage';
import { AgentSettingsPage } from '../../pages/agent/AgentSettingsPage';
import { AgentKnowledgeBasePage } from '../../pages/agent/AgentKnowledgeBasePage';
import { AgentConversationsPage } from '../../pages/agent/AgentConversationsPage';
import { CampaignBlueprintPage } from '../../pages/CampaignBlueprintPage';
import { useDashboard } from '../../context/DashboardContext';

interface AppShellProps {
  onLogout: () => void;
}

const navItems = [
  { label: 'Overview', path: '/', icon: LayoutDashboard },
  { label: 'Campaigns', path: '/campaigns', icon: MessageSquare },
  { label: 'Campaign Blueprint', path: '/campaign-blueprint', icon: Database },
  { label: 'Channels', path: '/whatsapp', icon: ShieldCheck },
  { label: 'Customers', path: '/customers', icon: Users },
  { label: 'TeleSales Conversations', path: '/agent/conversations', icon: MessageSquare },
  { label: 'TeleSales Settings', path: '/agent/settings', icon: Settings },
  { label: 'TeleSales Knowledge', path: '/agent/knowledge', icon: Database },
  { label: 'Notifications', path: '/notifications', icon: Bell },
  // { label: 'Events', path: '/events', icon: Calendar },
  { label: 'Error Logs', path: '/logs', icon: AlertTriangle },
];

export const AppShell: React.FC<AppShellProps> = ({ onLogout }) => {
  const { whatsappStatus } = useDashboard();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <div className="dashboard-shell">
      <aside className={`sidebar ${mobileNavOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-brand">
          <div className="logo-mark">T</div>
          <div>
            <p className="brand-title">Tamiyouz CRM</p>
            <span className="brand-subtitle">Operations Hub</span>
          </div>
          <button className="sidebar-close" onClick={() => setMobileNavOpen(false)}>
            <X size={16} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map(({ label, path, icon: Icon }) => (
            <NavLink
              key={path}
              to={path}
              end={path === '/'}
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              onClick={() => setMobileNavOpen(false)}
            >
              <Icon size={18} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button className="logout-pill" onClick={onLogout}>
            <LogOut size={16} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <div className="main-area">
        <header className="topbar">
          <button className="mobile-trigger" onClick={() => setMobileNavOpen(true)}>
            <Menu size={20} />
          </button>
          <div>
            <p className="eyebrow">Control Center</p>
            <h1>Customer Conversations</h1>
          </div>
          <div className="topbar-status">
            {/* <span className={`status-dot ${whatsappStatus.isReady ? 'online' : 'offline'}`} /> */}
            {/* <span>{whatsappStatus.isReady ? 'WhatsApp synced' : 'Device waiting to sync'}</span> */}
            {whatsappStatus.phoneNumber && <span className="phone-chip">{whatsappStatus.phoneNumber}</span>}
            <button className="ghost-button" onClick={onLogout}>
              <LogOut size={18} />
              <span>Sign out</span>
            </button>
          </div>
        </header>

        <div className="page-wrapper">
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/campaigns" element={<CampaignsPage />} />
            <Route path="/campaign-blueprint" element={<CampaignBlueprintPage />} />
            <Route path="/whatsapp" element={<WhatsAppConnectionPage />} />
            <Route path="/customers" element={<CustomersPage />} />
            <Route path="/messages" element={<Navigate to="/agent/conversations" replace />} />
            <Route path="/automation" element={<AutomationPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/logs" element={<ErrorLogsPage />} />
            <Route path="/agent" element={<Navigate to="/agent/conversations" replace />} />
            <Route path="/agent/whatsapp" element={<Navigate to="/whatsapp" replace />} />
            <Route path="/agent/settings" element={<AgentSettingsPage />} />
            <Route path="/agent/knowledge" element={<AgentKnowledgeBasePage />} />
            <Route path="/agent/conversations" element={<AgentConversationsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};
